import { Component, ViewEncapsulation, OnInit, AfterViewInit, Output, EventEmitter } from '@angular/core';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { UserService } from 'src/app/services/user/user.service';
import { UserModalService } from 'src/app/services/user-modal/user-modal.service';
import { Client, PaginationInfo, PaginatedResponse, User } from 'src/app/shared/models';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { USER_TYPES } from 'src/app/config/system.config';
import { ClientService } from 'src/app/services/user/client.service';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
import { Router } from '@angular/router';
declare var KTMenu: any;
declare var bootstrap: any;

interface SalesPersonOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-client-manage-list',
  templateUrl: './client-manage-list.component.html',
  styleUrls: ['./client-manage-list.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ClientManageListComponent extends DestructorComponent implements OnInit, AfterViewInit {

  @Output() clientUpdated = new EventEmitter<void>();

  // Data properties
  clients: Client[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;
  showClientFiltersForm = false;
  salesPersonList: SalesPersonOption[] = [];
  selectedStatusFilter: string = 'all';

  // Filter counts
  totalClients: number = 0;
  blockedCount: number = 0;
  permittedCount: number = 0;

  // Login as client state
  loginAsClientLoadingId: string | null = null;

  // Search subject for debouncing
  private searchSubject = new Subject<string>();

  constructor(
    private userService: UserService,
    private _clientService: ClientService,
    private userModalService: UserModalService,
    private _globalStateService: GlobalStateService,
    private _router:Router
  ) {
    super();
    this.setupSearchDebounce();
  }

  ngOnInit(): void {
    this.loadClients();
    this.loadSalesPersonList();
  }

  ngAfterViewInit(): void {
    // Initialize KTMenu instances for dropdown menus
    this.initializeMenus();
  }

  setupSearchDebounce(): void {
    this.subscriptions.push(
      this.searchSubject.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(searchTerm => {
        this.loadClients(1, searchTerm);
      })
    );
  }

  loadClients(page: number = this.currentPage, search: string = this.searchTerm): void {
    this.isLoading = true;
    this.currentPage = page;

    // Build filters object
    const filters: any = { role_type: USER_TYPES.CLIENT, system_user : "false" };
    if (this.selectedStatusFilter && this.selectedStatusFilter !== 'all') {
      filters.status = this.selectedStatusFilter;
    }

    this.subscriptions.push(
      this._clientService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<Client>) => {
          if (response.status && response.data) {
            this.clients = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;

            this.totalClients = response.data.pagination.total;
            this.blockedCount = response.data.pagination.blockedCount;
            this.permittedCount = response.data.pagination.permittedCount;
            this.refreshMenus();
          } else {
            this.clients = [];
            this.resetPagination();
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading clients:', error);
          this.clients = [];
          this.resetPagination();
          this.isLoading = false;
        }
      })
    );
  }

  resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  onSearch(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm = target.value;
    this.searchSubject.next(this.searchTerm);
  }

  onPageChange(page: number): void {
    this.loadClients(page);
  }

  onStatusFilterChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.selectedStatusFilter = target.value;
    this.loadClients(1); // Reset to first page when filtering
  }

  toggleClientFiltersForm() {
    this.showClientFiltersForm = !this.showClientFiltersForm;
  }

  loadSalesPersonList(): void {
    this.subscriptions.push(
      this.userService.GetData('1', '', {role_type: USER_TYPES.ADMIN, paginate: 'false'}).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            // Handle non-paginated response structure
            const salesPersons = Array.isArray(response.data) ? response.data : response.data.data || [];
            this.salesPersonList = salesPersons.map((person: User) => ({
              value: person._id || person.doc_id || '',
              label: `${person.title || ''} ${person.name || ''}`.trim()
            }));
          }
        },
        error: (error) => {
          console.error('Error loading sales person list:', error);
          this.salesPersonList = [];
        }
      })
    );
  }

  // Initialize KTMenu instances
  initializeMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 100);
  }

  // Refresh menus after data load
  refreshMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 100);
  }

  // Utility methods for template
  getAvatarDisplay(client: Client): { type: 'image' | 'initial', value: string, bgClass?: string } {
    if (client.avatar && client.avatar.trim() !== '') {
      return { type: 'image', value: client.avatar };
    }

    const name = client.name || client.email || 'C';
    const initial = name.charAt(0).toUpperCase();
    const bgClasses = ['bg-light-primary text-primary', 'bg-light-success text-success',
                      'bg-light-info text-info', 'bg-light-warning text-warning',
                      'bg-light-danger text-danger'];
    const bgClass = bgClasses[initial.charCodeAt(0) % bgClasses.length];

    return { type: 'initial', value: initial, bgClass };
  }

  // Track by function for ngFor performance
  trackByClientId(index: number, client: Client): string {
    return client._id || client.doc_id || index.toString();
  }

  // Method to handle add client
  addClient(): void {
    console.log('Add client');

    // Clear any selected user from service
    this.userModalService.clearSelectedUser();

    // Dispatch custom event for add client
    const addEvent = new CustomEvent('addClient', {
      bubbles: true
    });
    console.log('Dispatching addClient event:', addEvent);
    document.dispatchEvent(addEvent);

    // Small delay to ensure data is cleared
    setTimeout(() => {
      // Open the modal
      const modalElement = document.getElementById('kt_modal_edit_client');
      console.log('Modal element found:', modalElement);
      if (modalElement) {
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
      }
    }, 100);
  }

  // Method to handle edit client
  editClient(client: Client): void {
    console.log('Edit client:', client);

    // Use the service to set the selected user/client
    this.userModalService.setSelectedUser(client);

    // Also dispatch custom event as backup
    const editEvent = new CustomEvent('editClient', {
      detail: client,
      bubbles: true
    });
    console.log('Dispatching editClient event:', editEvent);
    document.dispatchEvent(editEvent);

    // Small delay to ensure data is set
    setTimeout(() => {
      // Open the modal
      const modalElement = document.getElementById('kt_modal_edit_client');
      console.log('Modal element found:', modalElement);
      if (modalElement) {
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
      }
    }, 100);
  }

  // Handle client updated event
  onClientUpdated(): void {
    this.loadClients(); // Refresh the clients list
    this.clientUpdated.emit(); // Emit event to parent component
  }

  // Method to handle login as client
  loginAsClient(client: Client): void {
    const clientId = client._id || client.doc_id;
    if (!clientId) {
      console.error('Client ID not found');
      return;
    }

    // Set loading state for this specific client
    this.loginAsClientLoadingId = clientId;

    this.subscriptions.push(
      this._clientService.LoginAsClient(clientId).subscribe({
        next: (response) => {
          console.log('Login as client response:', response);
          this.loginAsClientLoadingId = null;
          let ssoCode = response.data.ssoCode;
          window.open(`${this._globalStateService.websiteURL}/auth/sso?auth_code=${ssoCode}`, '_blank');
        },
        error: (error) => {
          console.error('Error logging in as client:', error);
          this.loginAsClientLoadingId = null;
        }
      })
    );
  }

  openBidHistory(client_id:any){

    window.open('/bid-manager/my-bids?client_id=' + client_id, '_blank');

  }

  viewDetailClient(client_id: any): void {
    if (client_id) {
      const url = this._router.serializeUrl(
        this._router.createUrlTree(['sales/client-manager', client_id])
      );

      window.open(url, '_blank');
    }
  }

}
